import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Truck, Package } from 'lucide-react';
import { API } from '../config';
import { MobileNav } from '../components/MobileNav';

const playfairFont = "'Playfair Display', serif";
const fmt = (n) => new Intl.NumberFormat('id-ID').format(Number(n) || 0);
const dateFmt = (iso) => iso ? new Date(iso).toLocaleString('id-ID') : '—';

const STATUS_BADGE = {
  pending_payment: { bg: 'rgba(234,179,8,0.2)', fg: '#EAB308', label: 'PENDING PAYMENT' },
  paid_pending_admin: { bg: 'rgba(59,130,246,0.2)', fg: '#3B82F6', label: 'PAID — AWAITING ADMIN' },
  approved: { bg: 'rgba(34,197,94,0.2)', fg: '#22C55E', label: 'APPROVED' },
  shipped: { bg: 'rgba(168,85,247,0.2)', fg: '#A855F7', label: 'SHIPPED' },
  delivered: { bg: 'rgba(34,197,94,0.3)', fg: '#22C55E', label: 'DELIVERED' },
  cancelled: { bg: 'rgba(239,68,68,0.2)', fg: '#EF4444', label: 'CANCELLED' },
};

export const KujiShippingList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/kuji/shipping/requests`, { withCredentials: true });
        setItems(data || []);
      } catch (_e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen pb-32" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }} data-testid="shipping-list-page">
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8, 12, 10, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <button onClick={() => navigate('/kuji/inventory')} className="flex items-center gap-2">
            <ArrowLeft className="w-5 h-5" style={{ color: '#F8FAF9' }} />
            <span style={{ color: '#F8FAF9' }}>Inventory</span>
          </button>
          <div className="flex items-center gap-2">
            <Truck className="w-6 h-6" style={{ color: '#D4AF37' }} />
            <h1 className="text-xl md:text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>My Shipments</h1>
          </div>
          <span className="w-12" />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {loading && <div className="text-center py-20"><div className="inline-block w-10 h-10 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} /></div>}
        {!loading && items.length === 0 && (
          <div className="text-center py-20 rounded-2xl backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <Package className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
            <p style={{ color: '#A3B8AC' }}>No shipping requests yet.</p>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="space-y-3" data-testid="shipping-list">
            {items.map((sr) => {
              const badge = STATUS_BADGE[sr.status] || STATUS_BADGE.pending_payment;
              return (
                <div
                  key={sr.id}
                  onClick={() => navigate(`/kuji/shipping/${sr.id}`)}
                  className="rounded-2xl p-4 cursor-pointer backdrop-blur-xl"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid={`shipping-row-${sr.id}`}
                >
                  <div className="flex justify-between items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-semibold" style={{ color: '#F8FAF9' }}>{sr.recipient_name}</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: badge.bg, color: badge.fg }}>
                          {badge.label}
                        </span>
                      </div>
                      <p className="text-xs truncate" style={{ color: '#A3B8AC' }}>{sr.address_line}{sr.district ? `, ${sr.district}` : ''}, {sr.city}, {sr.province}</p>
                      <p className="text-xs mt-1" style={{ color: '#A3B8AC' }}>{sr.item_count} item{sr.item_count > 1 ? 's' : ''} · {sr.courier?.toUpperCase()} {sr.service}</p>
                      {sr.tracking_number && <p className="text-xs mt-1" style={{ color: '#D4AF37' }}>Tracking: {sr.tracking_number}</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold" style={{ color: '#D4AF37' }}>{fmt(sr.shipping_cost)} {sr.payment_method === 'coins' ? 'coins' : 'IDR'}</p>
                      <p className="text-[10px]" style={{ color: '#A3B8AC' }}>{dateFmt(sr.created_at)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <MobileNav />
    </div>
  );
};

// ============ DETAIL ============

export const KujiShippingDetail = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/kuji/shipping/requests/${requestId}`, { withCredentials: true });
        setDoc(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load shipment');
      } finally {
        setLoading(false);
      }
    })();
  }, [requestId]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#080C0A' }}><div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} /></div>;
  }
  if (!doc) return null;

  const badge = STATUS_BADGE[doc.status] || STATUS_BADGE.pending_payment;

  return (
    <div className="min-h-screen pb-32" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }} data-testid="shipping-detail-page">
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8, 12, 10, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <button onClick={() => navigate('/kuji/shipping')} className="flex items-center gap-2">
            <ArrowLeft className="w-5 h-5" style={{ color: '#F8FAF9' }} />
            <span style={{ color: '#F8FAF9' }}>Shipments</span>
          </button>
          <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: badge.bg, color: badge.fg }}>
            {badge.label}
          </span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-6 space-y-4">
        <section className="rounded-2xl p-5 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 className="text-sm uppercase tracking-wider mb-3" style={{ color: '#D4AF37' }}>Recipient</h2>
          <p style={{ color: '#F8FAF9' }}><strong>{doc.recipient_name}</strong></p>
          <p style={{ color: '#A3B8AC' }}>{doc.recipient_phone}</p>
          <p style={{ color: '#A3B8AC' }} className="mt-2">{doc.address_line}</p>
          {doc.district && <p style={{ color: '#A3B8AC' }}>Kecamatan: {doc.district}</p>}
          <p style={{ color: '#A3B8AC' }}>{doc.city}, {doc.province} {doc.postal_code}</p>
        </section>

        <section className="rounded-2xl p-5 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 className="text-sm uppercase tracking-wider mb-3" style={{ color: '#D4AF37' }}>Shipment</h2>
          <div className="flex justify-between text-sm mb-1">
            <span style={{ color: '#A3B8AC' }}>Courier</span>
            <span style={{ color: '#F8FAF9' }}>{doc.courier?.toUpperCase()} {doc.service}</span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span style={{ color: '#A3B8AC' }}>Items</span>
            <span style={{ color: '#F8FAF9' }}>{doc.item_count}</span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span style={{ color: '#A3B8AC' }}>Shipping cost</span>
            <span style={{ color: '#D4AF37' }}>{fmt(doc.shipping_cost)} {doc.payment_method === 'coins' ? 'coins' : 'IDR'}</span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span style={{ color: '#A3B8AC' }}>Payment</span>
            <span style={{ color: '#F8FAF9' }}>{doc.payment_method?.toUpperCase()}</span>
          </div>
          {doc.tracking_number && (
            <div className="flex justify-between text-sm mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ color: '#A3B8AC' }}>Tracking</span>
              <span className="font-mono" style={{ color: '#D4AF37' }} data-testid="tracking-number">{doc.tracking_number}</span>
            </div>
          )}
        </section>

        {doc.notes && (
          <section className="rounded-2xl p-5 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h2 className="text-sm uppercase tracking-wider mb-2" style={{ color: '#D4AF37' }}>Notes</h2>
            <p style={{ color: '#A3B8AC' }} className="text-sm">{doc.notes}</p>
          </section>
        )}

        {doc.admin_note && (
          <section className="rounded-2xl p-5 backdrop-blur-xl" style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.2)' }}>
            <h2 className="text-sm uppercase tracking-wider mb-2" style={{ color: '#22C55E' }}>Admin note</h2>
            <p style={{ color: '#F8FAF9' }} className="text-sm">{doc.admin_note}</p>
          </section>
        )}

        <p className="text-xs text-center" style={{ color: '#A3B8AC' }}>
          Created {dateFmt(doc.created_at)}{doc.updated_at !== doc.created_at ? ` · Updated ${dateFmt(doc.updated_at)}` : ''}
        </p>
      </div>

      <MobileNav />
    </div>
  );
};
